import React from 'react';

/**
 * The rail: where you are, and where you can go.
 *
 * <h3>Five, not two</h3>
 *
 * The approved surface has five destinations. A rail that grew an icon each
 * time a screen landed would read as unfinished on every visit, so all five are
 * drawn and the three without a screen yet are disabled — "later" rather than
 * "broken", and a disabled control cannot take anyone to a page that is not
 * there.
 *
 * The estate is disabled too until there is one. An empty dashboard reads as a
 * fault; nothing to show should mean nowhere to go.
 *
 * <h3>Run is reachable without a selection</h3>
 *
 * Trust and Change describe one agent and need one chosen first. The Run Center
 * does its own choosing, from the agents the platform says serve governed
 * traffic, so it opens as soon as one of those exists.
 */
export default function ProtectRail({
  view = 'front', estateCount = 0, selected = false, runnable = 0, onGo,
}) {
  /* A shut control says why it is shut, in its own words: the three agent-shaped
     screens are shut for a different reason than the Run Center is, and the
     wrong reason sends somebody looking for a control that is not there. */
  const items = [
    { key: 'front', label: 'Protect an agent', icon: Shield, live: true },
    { key: 'dashboard', label: 'Your agents', icon: Bars, live: estateCount > 0,
      why: 'nothing protected yet' },
    /* Trust and Change are about one agent. Without one chosen they have no
       subject, so the rail keeps them shut rather than opening an empty page. */
    { key: 'trust', label: 'Trust', icon: Doc, live: selected,
      why: 'choose an agent first' },
    /* Run is not. The Run Center chooses its own agent, from the ones the
       platform says serve governed traffic, so it opens whenever there is at
       least one — reaching it should not require having just built something. */
    { key: 'run', label: 'Run', icon: Play, live: runnable > 0 || selected,
      why: 'no agent is published or execution ready yet' },
    { key: 'change', label: 'Change', icon: Gear, live: selected,
      why: 'choose an agent first' },
  ];

  return (
    <nav
      aria-label="Protect"
      style={{
        background: 'var(--p7-surface)',
        borderRight: '1px solid var(--p7-line)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px',
        padding: '12px 0', position: 'sticky', top: 0, height: '100vh', width: '56px',
        flex: 'none',
      }}
    >
      {/* No mark here: the header lockup already carries one, and two of the
          same logo a few pixels apart reads as a mistake rather than branding. */}
      {items.map(({ key, label, icon: Icon, live, why }) => {
        const here = view === key;
        return (
          <button
            key={key}
            type="button"
            title={live ? label : `${label} — ${why || 'not ready yet'}`}
            aria-label={label}
            aria-current={here ? 'page' : undefined}
            disabled={!live}
            onClick={() => live && onGo && onGo(key)}
            style={{
              width: '38px', height: '38px', border: 0, borderRadius: '9px',
              display: 'grid', placeItems: 'center',
              cursor: live ? 'pointer' : 'default',
              background: here ? 'var(--p7-accent-bg)' : 'transparent',
              color: here ? 'var(--p7-accent)'
                : live ? 'var(--p7-ink-subtle)' : 'var(--p7-ink-faint)',
              opacity: live ? 1 : 0.45,
            }}
          >
            <Icon />
          </button>
        );
      })}
    </nav>
  );
}

const svg = (children) => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const Shield = () => svg(<path d="M12 3 4 7v5c0 4.4 3.4 8.3 8 9 4.6-.7 8-4.6 8-9V7l-8-4Z" />);
const Bars = () => svg(<path d="M4 19V9M10 19V5M16 19v-7M21 19H3" />);
const Doc = () => svg(<><path d="M6 4h9l4 4v12H6z" /><path d="M9 12h7M9 16h5" /></>);
const Play = () => svg(<><path d="M21 12a8 8 0 1 1-3-6.2" /><path d="M8 12h6M12 9l3 3-3 3" /></>);
const Gear = () => svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" /></>);
